import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';

import AccountPickerContext from '../../contexts/AccountPickerContext';
import NotificationFactory from '../../NotificationFactory';
import CurrentUserContext from '../../contexts/CurrentUserContext';
import NotificationsContext from '../../contexts/NotificationsContext';
import {
  buildShowWorkSpace,
  buildShowGlobalGoal,
  buildShowGlobalKpiList,
} from '../../navigation/appRoutes';
import setTask from './setTask';
import deleteTask from './deleteTask';
import getTasksSnapshot from './getTasksSnapshot';
import { buildTabLink } from 'components/WorkSpaces/ShowTopBarTabs';
import { PortalsContext } from '../SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import AvailableWorkSpacesContext from '../../contexts/AvailableWorkSpacesContext';
import { isTargetsApp } from '../../isWorkSpace';
import { V3AppsContext } from '../SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import { UIVersionContext } from '../SplashScreenProviders/UserAndAccountProviders/UIVersionProvider';
import appNavItemTypeCheckers from '../../types/appNavItemTypeCheckers';

const useTasks = (
  sources: Tasks.TaskSource[],
  destination: Tasks.TaskSource | undefined,
) => {
  const { selectedPortal } = useContext(PortalsContext);
  const { sendNotification } = useContext(NotificationsContext);
  const { availableWorkSpaces: v1v2Workspaces } = useContext(
    AvailableWorkSpacesContext,
  );
  const { allWorkspaces: v3Workspaces } = useContext(V3AppsContext);
  const currentUser = useContext(CurrentUserContext);
  const { isWallboardUser } = useContext(CurrentUserContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { isV3 } = useContext(UIVersionContext);
  const [tasks, setTasks] = useState<Tasks.Task[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const availableWorkSpaces = useMemo((): (
    | V3.Workspace
    | WorkSpace
    | TargetsApp.App
  )[] => {
    if (isV3) {
      return v3Workspaces;
    }
    return v1v2Workspaces;
  }, [isV3, v1v2Workspaces, v3Workspaces]);

  useEffect(() => {
    if (isWallboardUser) {
      setIsLoading(false);
      return;
    }

    const sourceIds = sources.map((s) => s.id);
    if (sourceIds.length === 0) {
      setIsLoading(false);
      return;
    }

    let isActive = true;

    const query = getTasksSnapshot({
      accountId: selectedAccountId,
    }).where('taskableId', 'in', sourceIds);

    const unsubscribe = query.onSnapshot(
      (snapshot: firebase.default.firestore.QuerySnapshot) => {
        if (!isActive) {
          return;
        }
        const data: Tasks.Task[] = [];
        snapshot.docs.forEach((d) =>
          data.push({ id: d.id, ...d.data() } as Tasks.Task),
        );

        const sourceTypes = new Set(sources.map((s) => s.type));
        const filtered = data.filter((task) =>
          sourceTypes.has(task.taskableType),
        );

        setTasks(_.sortBy(filtered, 'createdOn'));
        setIsLoading(false);
      },
    );

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [isWallboardUser, selectedAccountId, sources]);

  const buildItemLink = useCallback(
    (task: Tasks.Task) => {
      if (task.taskableType === 'Goal') {
        return buildShowGlobalGoal(task.taskableId);
      } else if (task.taskableType === 'Scorecard') {
        return buildShowGlobalKpiList({
          scorecardId: task.taskableId,
          portal: selectedPortal,
        });
      } else if (task.taskableType === 'WorkSpace') {
        const workSpace = availableWorkSpaces.find(
          (ws) => ws.id === task.taskableId,
        );
        if (!workSpace) {
          return buildShowWorkSpace('');
        }

        if ('navigationSettings' in workSpace) {
          let tasksItem: V3.Navigation.Tasks | undefined;
          workSpace.navigationSettings.sections.forEach((section) => {
            section.items.forEach((item) => {
              if (appNavItemTypeCheckers.isTasks(item)) {
                tasksItem = item;
              }
            });
          });

          if (tasksItem) {
            return `workspaces/${workSpace.id}/${tasksItem.key}`;
          }

          return `workspaces/${workSpace.id}`;
        }

        const tab = (() => {
          if (isTargetsApp(workSpace)) {
            return workSpace.tabs.find((t) => t.type === 'tasks');
          }
          return workSpace.tabs.find((t) => t.type === 'tasks');
        })();

        return buildTabLink({
          workSpace,
          tab,
          portal: selectedPortal,
        });
      } else {
        if (process.env.NODE_ENV === 'development') {
          const error = new Error();
          error.name = `Unknown type: ${task.taskableType}`;
          throw error;
        }
        return undefined;
      }
    },
    [availableWorkSpaces, selectedPortal],
  );

  const sendCreationNotifications = useCallback(
    async (newTask: Tasks.Task) => {
      const link = buildItemLink(newTask);
      if (!link) {
        return;
      }
      const assignedN = NotificationFactory.assignedToTask({
        taskCreatorName: currentUser.displayName,
        link,
        task: newTask,
      });

      await sendNotification(assignedN, newTask.assignedTo);
    },
    [currentUser.displayName, buildItemLink, sendNotification],
  );

  const sendUpdatedNotifications = useCallback(
    async (newTask: Tasks.Task, oldTask: Tasks.Task) => {
      if (newTask.assignedTo === oldTask.assignedTo) {
        return;
      }
      const link = buildItemLink(newTask);
      if (!link) {
        return;
      }
      const assignedN = NotificationFactory.assignedToTask({
        taskCreatorName: currentUser.displayName,
        link,
        task: newTask,
      });
      const unAssignedN = NotificationFactory.unAssignedFromTask({
        link,
        task: newTask,
      });
      await sendNotification(assignedN, newTask.assignedTo);
      await sendNotification(unAssignedN, oldTask.assignedTo);
    },
    [currentUser.displayName, buildItemLink, sendNotification],
  );

  const onTaskDeleted = useCallback(
    async (task: Tasks.Task) => {
      await deleteTask(task, selectedAccountId);
    },
    [selectedAccountId],
  );

  const onTaskCompletedToggled = useCallback(
    async (task: Tasks.Task) => {
      const newTask = {
        ...task,
        isComplete: !task.isComplete,
      };

      await setTask(newTask, selectedAccountId);
    },
    [selectedAccountId],
  );

  const onTaskUpdated = useCallback(
    async (updatedTask: Tasks.Task, oldTask: Tasks.Task) => {
      await setTask(updatedTask, selectedAccountId);
      await sendUpdatedNotifications(updatedTask, oldTask);
    },
    [selectedAccountId, sendUpdatedNotifications],
  );

  const onTaskCreated = useCallback(
    async (newTaskProps: Tasks.TaskCreationProps) => {
      if (!destination) {
        return;
      }
      const newTask = {
        ...newTaskProps,
        taskableType: destination.type,
        taskableId: destination.id,
      };
      await setTask(newTask, selectedAccountId);
      await sendCreationNotifications(newTask);
    },
    [selectedAccountId, destination, sendCreationNotifications],
  );

  return {
    tasks,
    isLoading,
    onTaskCompletedToggled,
    onTaskUpdated,
    onTaskCreated: destination ? onTaskCreated : undefined,
    onTaskDeleted,
  };
};

export default useTasks;
